import { useSearchParams } from 'react-router-dom';
import { useTournaments } from '../api/hooks';
import { ErrorView, EmptyView } from '../components/StateViews';
import { BlueHeader } from '../components/ScreenShell';
import { TournamentCard, PastTournamentCard } from '../components/TournamentCard';
import { haptics } from '../telegram';
import type { Tournament } from '../api/types';

const monthFmt = new Intl.DateTimeFormat('ru-RU', { month: 'long' });

type Group = { label: string; accent: boolean; items: Tournament[] };

function monthLabel(iso: string): string {
  const d = new Date(iso);
  const m = monthFmt.format(d);
  const name = m.charAt(0).toUpperCase() + m.slice(1);
  return d.getFullYear() === new Date().getFullYear() ? name : `${name} ${d.getFullYear()}`;
}

/** Upcoming: "Сейчас идёт" → "Эта неделя" (ближайшие 7 дней) → по месяцам. */
function groupUpcoming(items: Tournament[]): Group[] {
  const weekEnd = Date.now() + 7 * 24 * 3600 * 1000;
  const groups: (Group & { key: string })[] = [];
  for (const t of items) {
    const ts = new Date(t.startTime).getTime();
    let key: string, label: string, accent: boolean;
    if (t.status === 'in_progress') {
      key = 'live';
      label = 'Сейчас идёт';
      accent = true;
    } else if (ts <= weekEnd) {
      key = 'week';
      label = 'Эта неделя';
      accent = true;
    } else {
      const d = new Date(t.startTime);
      key = `${d.getFullYear()}-${d.getMonth()}`;
      label = monthLabel(t.startTime);
      accent = false;
    }
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label, accent, items: [] };
      groups.push(g);
    }
    g.items.push(t);
  }
  return groups;
}

/** Past: newest-first, plain month groups (year added when not the current one). */
function groupPast(items: Tournament[]): Group[] {
  const groups: (Group & { key: string })[] = [];
  for (const t of items) {
    const d = new Date(t.startTime);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: monthLabel(t.startTime), accent: false, items: [] };
      groups.push(g);
    }
    g.items.push(t);
  }
  return groups;
}

/** T1 «Предстоящие / Прошедшие». */
export function ScheduleScreen() {
  // Таб живёт в URL (?tab=past), а не в useState: при возврате со страницы
  // турнира компонент ремоунтится, и локальный стейт сбрасывался бы.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: 'upcoming' | 'past' = searchParams.get('tab') === 'past' ? 'past' : 'upcoming';
  const { data, isLoading, isError, error } = useTournaments(tab);

  const pick = (next: 'upcoming' | 'past') => {
    if (next === tab) return;
    haptics.tap();
    setSearchParams(next === 'past' ? { tab: 'past' } : {}, { replace: true });
  };

  return (
    <div>
      <BlueHeader>
        <div className="bhead-title">Расписание</div>
      </BlueHeader>

      <div className="content">
      <div className="segmented">
        <button className={tab === 'upcoming' ? 'active' : ''} onClick={() => pick('upcoming')}>
          Предстоящие
        </button>
        <button className={tab === 'past' ? 'active' : ''} onClick={() => pick('past')}>
          Прошедшие
        </button>
      </div>

      {isLoading ? (
        <ScheduleSkeleton />
      ) : isError ? (
        <ErrorView message={(error as Error)?.message} />
      ) : !data || data.length === 0 ? (
        <EmptyView
          message={tab === 'past' ? 'Пока нет прошедших турниров' : 'Ближайших турниров пока нет'}
        />
      ) : tab === 'upcoming' ? (
        groupUpcoming(data).map((g) => (
          <div key={g.label}>
            <div className={`date-group ${g.accent ? 'date-group--accent' : ''}`}>{g.label}</div>
            {g.items.map((t) => (
              <TournamentCard key={t.id} t={t} />
            ))}
          </div>
        ))
      ) : (
        groupPast(data).map((g) => (
          <div key={g.label}>
            <div className="date-group">{g.label}</div>
            {g.items.map((t) => (
              <PastTournamentCard key={t.id} t={t} />
            ))}
          </div>
        ))
      )}
      </div>
    </div>
  );
}

function ScheduleSkeleton() {
  return (
    <div>
      <div className="skeleton" style={{ height: 12, width: 90, borderRadius: 6, margin: '4px 0 12px' }} />
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 92, borderRadius: 18, marginBottom: 12 }} />
      ))}
    </div>
  );
}
